<template>
 <app-filters-drawer :model-value="filtersPaneIsOpen" :title="$t('project.question-answer.QuestionAnswerFiltersPane.filters')"
                     data-locator="qanda-filters-pane"
                     @close="closePane" @clear="onClearAllFilters" @apply="onApplyFilters"
 >
   <template #body>
     <v-row>
       <v-col>
         <div class="font-weight-bold mb-3 text-text-primary">
           {{ $t('project.question-answer.QuestionAnswerFiltersPane.priority') }}
         </div>
         <v-row class="checkboxes-container">
           <v-col
            v-for="(priority, i) in priorityList"
            :key="`priority-${i}`"
            class="py-2"
            cols="12"
            md="6"
           >
             <v-checkbox
                v-model="localFilters.priority"
                :label="getPriorityLabel(priority)"
                :value="priority"
                density="compact"
                hide-details
                class="mt-0 pt-0"
                :data-locator="`filter-priority-${priority}`"
             />
           </v-col>
         </v-row>
       </v-col>
     </v-row>
     <template v-if="categoriesAsObject && !categoriesPending">
       <v-row>
         <v-col>
           <div class="font-weight-bold mb-3 text-text-primary">
             {{ $t('project.question-answer.QuestionAnswerFiltersPane.category') }}
           </div>
           <v-row class="checkboxes-container">
             <v-col v-for="(categoryName, categoryId) in categoriesAsObject"
                    :key="`category-${categoryId}`"
                    class="py-2"
                    cols="12"
                    md="6"
             >
               <!-- we need to cast the category's id value -->
               <!-- if not, a string is stored in the filters's state and can't match when comparing with category.id (which is a number) -->
               <v-checkbox v-model="localFilters.categories"
                           :label="categoryName"
                           :value="+categoryId"
                           density="compact"
                           hide-details
                           class="mt-0 pt-0"
                           :data-locator="`filter-category-${categoryId}`"
               />
             </v-col>
           </v-row>
         </v-col>
       </v-row>
      <v-row>
        <v-col>
          <v-divider class="my-2"/>
        </v-col>
      </v-row>
     </template>
     <v-row>
       <v-col>
         <div class="font-weight-bold mb-3 text-text-primary">
           {{ $t('project.question-answer.QuestionAnswerFiltersPane.questioner') }}
         </div>
         <v-row class="checkboxes-container">
           <v-col v-for="(questioner, i) in questioners"
                  :key="`questioner-${i}`"
                  class="py-2"
                  cols="12"
                  md="6"
           >
             <v-checkbox v-model="localFilters.questioners"
                         :label="getQuestionerLabel(questioner)"
                         :value="questioner"
                         density="compact"
                         hide-details
                         class="mt-0 pt-0"
                          :data-locator="`filter-questioner-${questioner}`"
             />
           </v-col>
         </v-row>
       </v-col>
     </v-row>
     <v-row>
       <v-col>
         <v-divider class="my-2"/>
       </v-col>
     </v-row>
     <v-row>
       <v-col>
         <div class="font-weight-bold mb-3 text-text-primary">{{$t('project.question-answer.QuestionAnswerFiltersPane.createdAt')}}</div>
         <AppDateRangePicker v-model="localFilters.createdAt" data-locator="filter-created-at" />
       </v-col>
     </v-row>
    <v-row>
      <v-col>
        <v-divider class="my-2"/>
      </v-col>
    </v-row>
     <v-row>
       <v-col>
         <div class="font-weight-bold mb-3 text-text-primary">{{$t('project.question-answer.QuestionAnswerFiltersPane.lastUpdate')}}</div>
         <AppDateRangePicker v-model="localFilters.lastUpdate" data-locator="filter-last-update" />
       </v-col>
     </v-row>
      <v-row>
        <v-col>
          <v-divider class="my-2"/>
        </v-col>
      </v-row>
     <v-row>
       <v-col>
         <div class="font-weight-bold mb-3 text-text-primary">{{$t('project.question-answer.QuestionAnswerFiltersPane.status')}}</div>
         <v-row class="checkboxes-container">
           <v-col v-for="(status, i) in statusList"
                  :key="`status-${i}`"
                  class="py-2"
                  cols="12"
                  md="6"
           >
             <v-checkbox v-model="localFilters.status"
                         :label="$t(`common.qaStatus.${status}`)"
                         :value="status"
                         density="compact"
                         hide-details
                         class="mt-0 pt-0"
                        :data-locator="`filter-status-${status}`"
             />
           </v-col>
         </v-row>
       </v-col>
     </v-row>
   </template>
 </app-filters-drawer>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import AppDateRangePicker from '@/common/AppDateRangePicker.vue'
import { ISOToShortenedDate } from '@/common/utils/dates'
import { QA_STATUS } from '@/common/utils/qa-status'
import { Filter } from '@/models/filter.model'
import { QA_PRIORITIES, getQAPriorityMeta } from '@/project/question-answer/constants/qaPriority'
import { QuestionAnswerActionTypes } from '@/store/modules/question-answer/action_types'
import { QA_HIDDEN_AUTHOR_KEY } from '@/store/modules/question-answer/getters'
import { QuestionAnswerMutationTypes } from '@/store/modules/question-answer/mutation_types'

const EMPTY_FILTERS = {
  categories: [],
  questioners: [],
  createdAt: [],
  lastUpdate: [],
  status: [],
  priority: [],
}

export default {
  name: 'QuestionAnswerFiltersPane',
  components: {
    AppDateRangePicker,
  },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      localFilters: { ...EMPTY_FILTERS },
      statusList: Object.values(QA_STATUS),
      priorityList: Object.values(QA_PRIORITIES),
      QA_HIDDEN_AUTHOR_KEY,
    }
  },
  computed: {
    ...mapState('questionAnswer', ['filtersPaneIsOpen', 'filters', 'categoriesPending', 'questions']),
    ...mapGetters('questionAnswer', ['questioners', 'categoriesAsObject']),
  },
  watch: {
    filtersPaneIsOpen (value) {
      if (value) {
        this.syncAppliedFiltersWithLocals()
        this.GET_CATEGORIES(this.mnemo)
      }
    },
  },
  methods: {
    ...mapActions('questionAnswer', [QuestionAnswerActionTypes.GET_CATEGORIES]),
    ...mapMutations('questionAnswer', [QuestionAnswerMutationTypes.SET_FILTERS, QuestionAnswerMutationTypes.SET_FILTERS_PANE_IS_OPEN]),
    closePane () {
      this.SET_FILTERS_PANE_IS_OPEN(false)
    },
    getQuestionerLabel (questioner) {
      if (questioner === this.QA_HIDDEN_AUTHOR_KEY) {
        return this.$t('project.question-answer.QuestionAnswerDataTable.hiddenAuthor')
      }
      return questioner
    },
    applyFilters () {
      const categoriesFilters = this.localFilters.categories.map(categoryId =>
        new Filter(
          'categories',
          this.$t('project.question-answer.QuestionAnswerFiltersPane.categoryFilter'),
          categoryId,
          this.categoriesAsObject[categoryId],
        ),
      )
      const questionersFilters = this.localFilters.questioners.map(questioner =>
        new Filter(
          'questioners',
          this.$t('project.question-answer.QuestionAnswerFiltersPane.questionerFilter'),
          questioner,
          this.getQuestionerLabel(questioner),
        ),
      )
      const createdAtFilter = this.localFilters.createdAt.length > 0
        ? [
          new Filter(
            'createdAt',
            this.$t('project.question-answer.QuestionAnswerFiltersPane.createdAt'),
            this.localFilters.createdAt,
            `${ISOToShortenedDate(this.localFilters.createdAt[0])} - ${ISOToShortenedDate(this.localFilters.createdAt[1])}`,
          )]
        : []
      const lastUpdateFilter = this.localFilters.lastUpdate.length > 0
        ? [
          new Filter(
            'lastUpdate',
            this.$t('project.question-answer.QuestionAnswerFiltersPane.lastUpdate'),
            this.localFilters.lastUpdate,
            `${ISOToShortenedDate(this.localFilters.lastUpdate[0])} - ${ISOToShortenedDate(this.localFilters.lastUpdate[1])}`,
          )]
        : []
      const statusFilters = this.localFilters.status.map(status =>
        new Filter('status', this.$t('project.question-answer.QuestionAnswerFiltersPane.statusFilter'), status, this.$t(`common.qaStatus.${status}`)),
      )
      const priorityFilters = this.localFilters.priority.map(priority =>
        new Filter('priority', this.$t('project.question-answer.QuestionAnswerFiltersPane.priorityFilter'), priority, this.getPriorityLabel(priority)),
      )

      const filters = [
        ...categoriesFilters,
        ...questionersFilters,
        ...createdAtFilter,
        ...lastUpdateFilter,
        ...statusFilters,
        ...priorityFilters,
      ]

      this.SET_FILTERS(filters)
    },
    syncAppliedFiltersWithLocals () {
      this.localFilters.categories = this.filters
        .filter(filter => filter.category.key === 'categories')
        .map(filter => filter.value.key)

      this.localFilters.questioners = this.filters
        .filter(filter => filter.category.key === 'questioners')
        .map(filter => filter.value.key)

      const createdAtFilter = this.filters.find(filter => filter.category.key === 'createdAt')
      this.localFilters.createdAt = createdAtFilter?.value.key ?? []

      const lastUpdateFilter = this.filters.find(filter => filter.category.key === 'lastUpdate')
      this.localFilters.lastUpdate = lastUpdateFilter?.value.key ?? []

      this.localFilters.status = this.filters
        .filter(filter => filter.category.key === 'status')
        .map(filter => filter.value.key)

      this.localFilters.priority = this.filters
        .filter(filter => filter.category.key === 'priority')
        .map(filter => filter.value.key)
    },
    getPriorityLabel (priority) {
      const meta = getQAPriorityMeta(priority, this.$t)
      return meta.label
    },
    onApplyFilters () {
      this.applyFilters()
      this.closePane()
    },
    onClearAllFilters () {
      this.localFilters = { ...EMPTY_FILTERS }
      this.applyFilters()
    },
  },
}
</script>

<style scoped lang="scss">
.checkboxes-container {
  max-height: calc(36px * 5);
  overflow-y: auto;
}
</style>
